import React, {useContext, ReactElement} from 'react'
import {get, flowRight as compose} from 'lodash'
import {Tab, Tabs, Button, withStyles, WithStyles} from '@material-ui/core'
import {withRouter, Redirect, RouteComponentProps} from 'react-router-dom'
import {graphql} from 'react-apollo'
import {withNamespaces, Trans, WithNamespaces} from 'react-i18next'

import {Loading} from '../../../Common/Loading'
import PageTitle from '../../../Common/PageTitle'
import messages from '../../../../assets/messages'
import {ACCOUNTS_QUERY} from '../../../../graphql/queries'
import {Account} from '../../../../graphql/types'
import {isMobile} from '../../../../common/utils/browser'
import PageBody from '../../../Common/PageBody'
import AppContext from '../../../Common/contexts/AppContext'
import {IbAccountDetails} from './types'
import {useQueryApiV2} from '../../../../shared/hooks'
import {getAccountLabel} from '../../../../common/utils/accounts'
import {AccountOverview} from './components/AccountOverview'
import {styles} from './styles'
import {redirectToIBPortal} from './redirect-to-IB-portal'

type Props = WithNamespaces &
  WithStyles &
  RouteComponentProps & {
    account: Account
    accountLoading: boolean
  }

function IbAccountView(props: Props): ReactElement {
  const {accountLoading, account, t, history, location, classes} = props
  const {locale} = useContext(AppContext)
  const accountId = account?.id

  const [ibAccountDetails, {loading: ibDetailsLoading}] = useQueryApiV2<IbAccountDetails>({
    path: '/dashboard/ib/account-details',
    params: {accountId: accountId?.toString() ?? ''},
    skip: !accountId,
  })

  if (accountLoading || ibDetailsLoading) return <Loading />

  const tabs = [
    {
      element: <AccountOverview account={account} ibAccountDetails={ibAccountDetails} />,
      path: `/accounts/${account.id}`,
      label: t(messages.overview.i18nKey, messages.overview.defaults),
    },
    {
      element: <Redirect to="/transactions/withdraw" />,
      path: `/accounts/${account.id}/transactions`,
      label: t(messages.payments.i18nKey, messages.payments.defaults),
    },
  ]

  const tabIndex = tabs.findIndex((tab) => tab.path === location.pathname)
  const activeTab = tabIndex >= 0 ? tabs[tabIndex] : tabs[0]
  const label = getAccountLabel(account, locale)

  return (
    <>
      <PageTitle
        title={label}
        onBack={() => history.push('/accounts')}
        rightAction={
          <Button
            onClick={() => redirectToIBPortal(account)}
            variant="contained"
            color="primary"
            className={classes.portalButton}
          >
            IB portal
          </Button>
        }
        subtitle={<Trans {...messages.accountID} values={{accountId: account.remoteId}} />}
      >
        <Tabs variant={isMobile() ? 'scrollable' : 'standard'} value={tabIndex}>
          {tabs.map((tab, key) => (
            <Tab
              key={key}
              label={tab.label}
              textColor="inherit"
              onClick={() => history.push(tab.path)}
            />
          ))}
        </Tabs>
      </PageTitle>
      <PageBody>{activeTab.element}</PageBody>
    </>
  )
}

export default compose(
  withNamespaces(),
  withRouter,
  graphql(ACCOUNTS_QUERY, {
    options: (props: any) => ({
      variables: {id: Number(props.match.params.accountId)},
    }),
    props: ({data: {loading}, data}: any) => ({
      accountLoading: loading,
      account: get(data, 'viewer.accounts.0'),
    }),
  }),
  withStyles(styles, {withTheme: true}),
)(IbAccountView)
